import { LitElement, ReactiveController, ReactiveControllerHost } from 'lit';
import { LOGOUT_URL } from '../constants';
import { FlagSet } from '../feature-flags';

const PLATFORM_INACTIVITY_FLAG = 'platform-inactivity';

/** Duration of inactivity (in ms) before the user is redirected to logout. */
export const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes

/** How far before the timeout (in ms) we emit a warning event. */
export const WARNING_BEFORE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

/** How often (in ms) we poll to check whether the timeout has elapsed. */
export const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

/** Minimum interval (in ms) between updates to the last-activity timestamp. */
export const ACTIVITY_THROTTLE_MS = 5 * 1000; // 5 seconds

/**
 * User-activity events that reset the inactivity timer.
 * Listened on `document` so activity anywhere on the page is captured,
 * regardless of Shadow DOM boundaries.
 */
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

/**
 * Reactive controller that monitors user activity and redirects to
 * `/session/logout` after 20 minutes of inactivity.
 *
 * Only active when the `platform-inactivity` feature flag is `true`.
 * Flag values are piped in by the host via the `flags` setter.
 *
 * Resilient to:
 * - Computer sleep / hibernate (checks wall-clock time on wake via
 *   `visibilitychange` and `focus` events).
 * - Background-tab throttling (timestamp comparison is always accurate
 *   regardless of when the polling interval actually fires).
 * - System clock jumps forward (conservative — treats forward jumps as
 *   elapsed inactivity, which is the safe behaviour for a security timeout).
 */
export class InactivityController implements ReactiveController {
  host: ReactiveControllerHost & LitElement;

  private _lastActivity = Date.now();
  private _pollIntervalId: ReturnType<typeof setInterval> | undefined;
  private _lastThrottledUpdate = 0;
  private _active = false;
  private _warningEmitted = false;
  private _flags: FlagSet = {};

  /**
   * Called when the inactivity timeout expires. Defaults to navigating
   * to the logout URL. Can be overridden for testing.
   */
  onTimeout: () => void;

  constructor(host: LitElement) {
    (this.host = host).addController(this);

    this.onTimeout = () => {
      window.location.assign(LOGOUT_URL);
    };
  }

  // ---------------------------------------------------------------------------
  // Feature flag
  // ---------------------------------------------------------------------------

  /** Set the current feature flags. Called by the host when flags change. */
  set flags(value: FlagSet) {
    this._flags = value;
    this._syncMonitoring();
  }

  private get _enabled(): boolean {
    return this._flags[PLATFORM_INACTIVITY_FLAG] === true;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  hostConnected(): void {
    this._syncMonitoring();
  }

  hostDisconnected(): void {
    this._stopMonitoring();
  }

  // ---------------------------------------------------------------------------
  // Start / stop monitoring
  // ---------------------------------------------------------------------------

  private _startMonitoring(): void {
    if (this._active) return;
    this._active = true;

    this._lastActivity = Date.now();
    this._lastThrottledUpdate = 0;
    this._warningEmitted = false;

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, this._onActivity, { passive: true });
    }

    document.addEventListener('visibilitychange', this._onVisibilityChange);
    window.addEventListener('focus', this._checkTimeout);

    this._pollIntervalId = setInterval(this._checkTimeout, POLL_INTERVAL_MS);
  }

  private _stopMonitoring(): void {
    if (!this._active) return;
    this._active = false;

    for (const event of ACTIVITY_EVENTS) {
      document.removeEventListener(event, this._onActivity);
    }

    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('focus', this._checkTimeout);

    if (this._pollIntervalId !== undefined) {
      clearInterval(this._pollIntervalId);
      this._pollIntervalId = undefined;
    }
  }

  /** Start or stop monitoring based on the current flag value. */
  private _syncMonitoring(): void {
    if (this._enabled) {
      this._startMonitoring();
    } else {
      this._stopMonitoring();
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers (arrow functions to preserve `this`)
  // ---------------------------------------------------------------------------

  /** Throttled handler for user-activity events. */
  private _onActivity = (): void => {
    const now = Date.now();
    if (now - this._lastThrottledUpdate >= ACTIVITY_THROTTLE_MS) {
      this._lastActivity = now;
      this._lastThrottledUpdate = now;
      this._warningEmitted = false;
    }
  };

  /** When the tab becomes visible again check immediately. */
  private _onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this._checkTimeout();
    }
  };

  /** Compare wall-clock time against last activity and redirect if expired. */
  private _checkTimeout = (): void => {
    const elapsed = Date.now() - this._lastActivity;

    if (elapsed >= INACTIVITY_TIMEOUT_MS) {
      this._stopMonitoring();
      this.onTimeout();
      return;
    }

    if (!this._warningEmitted && elapsed >= INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS) {
      this._warningEmitted = true;
      this.host.dispatchEvent(
        new CustomEvent('platform-inactivity-warning', {
          bubbles: true,
          composed: true,
          detail: { secondsRemaining: WARNING_BEFORE_TIMEOUT_MS / 1000 },
        }),
      );
    }
  };
}
